/**
 * 调试工具 — 将 VM 暴露到浏览器控制台，方便开发调试
 *
 * 架构层次（链路要联通，层次要分明）：
 *   第1层 事件系统   — vm.execute('beforeSave')  细粒度生命周期钩子，回调返回 false 中断
 *   第2层 命令系统   — ttx.command.execute('save', { vm })  业务操作编排器，内部调事件系统 + API
 *   第3层 调试 API   — ttx.debug.save()  控制台快捷方法，路由到命令系统
 *
 * 开关控制：
 *   ttx.debug.open = true        // 开启调试（也可通过 URL ?debug=true 自动开启）
 *   ttx.debug.open = false       // 关闭调试
 *   ttx.debug.open               // 查看当前状态（true/false）
 *
 * 控制台使用（开启后）：
 *   ttx.debug.vm                  // 当前活跃的 ViewModel
 *   ttx.debug.vm.getData()        // 查看表单数据
 *   ttx.debug.save()              // 触发保存（走完整命令链路：beforeSave → API → afterSave）
 *   ttx.debug.edit()              // 切换到编辑态
 *   ttx.debug.cancel()            // 取消编辑
 *   ttx.debug.list()              // 列出所有 VM（key / title / mode）
 *   ttx.debug.vms                 // 所有 VM 的 key→vm 映射
 *
 * 低级调试（直接操作事件系统，跳过命令编排）：
 *   ttx.debug.vm.execute('beforeSave')         // 手动触发保存前校验
 *   ttx.debug.vm.execute('modeChange', 'edit') // 手动切换模式
 *   ttx.debug.vm.on('afterValueChange', fn)    // 监听字段值变更
 */
window.ttx = window.ttx || {};
ttx.debug = ttx.debug || {};

(function () {
    // ── 开关状态 ──
    // URL 带 ?debug=true 时自动开启，否则默认关闭
    var _enabled = (function () {
        try {
            var params = new URLSearchParams(window.location.search);
            return params.get('debug') === 'true';
        } catch (e) {
            return false;
        }
    })();

    // 内部注册表：key → { vm, title, key }
    // key 使用 paneKey（如 service010）而非 serviceCode，保证同 billno 下多个页签互不覆盖
    var _vms = {};
    // 当前活跃 VM 的 key，registerVM 时自动更新，unregisterVM 时自动回退
    var _currentKey = null;

    // ============================================================
    // 开关属性：ttx.debug.open = true / false
    // ============================================================

    Object.defineProperty(ttx.debug, 'open', {
        get: function () { return _enabled; },
        set: function (val) { _enabled = !!val; },
        enumerable: true,
        configurable: true
    });

    // ============================================================
    // VM 注册/注销（始终运行，不受开关影响，保证 core 层调用不报错）
    // ============================================================

    /**
     * 注册 VM，重复 key 会覆盖（同一页签刷新场景）
     * @param {string} key - 唯一标识，由 Meta 组件传入 this.props.id（pane key）
     * @param {object} vm - ViewModel 实例
     * @param {string} [title] - 页面标题
     */
    ttx.debug.registerVM = function (key, vm, title) {
        if (!key || !vm) return;
        _vms[key] = { vm: vm, title: title || '', key: key };
        _currentKey = key; // 新注册的自动成为当前 VM
    };

    /**
     * 注销 VM
     * 若注销的是当前活跃 VM，自动回退到最后一个剩余 VM；
     * 若无剩余 VM，_currentKey 置 null，ttx.debug.vm 返回 null
     */
    ttx.debug.unregisterVM = function (key) {
        if (!key) return;
        delete _vms[key];
        if (_currentKey === key) {
            var remaining = Object.keys(_vms);
            _currentKey = remaining.length > 0 ? remaining[remaining.length - 1] : null;
        }
    };

    /**
     * 按 key 获取已注册的 VM（不受 debug 开关控制）
     * 用于 getPageUrl 等功能获取当前页签的最新 VM（可能已从列表钻取到卡片）
     */
    ttx.debug.getVM = function (key) {
        if (!key) return null;
        var entry = _vms[key];
        return entry ? entry.vm : null;
    };

    /**
     * 获取当前活跃 VM（不受 debug 开关控制）
     * 用于 getPageUrl 等功能
     */
    ttx.debug.getCurrentVM = function () {
        if (_currentKey && _vms[_currentKey]) return _vms[_currentKey].vm;
        var keys = Object.keys(_vms);
        return keys.length > 0 ? _vms[keys[keys.length - 1]].vm : null;
    };

    // ============================================================
    // 动态属性：受开关控制，关闭时返回 null / {}
    // ============================================================

    Object.defineProperty(ttx.debug, 'vm', {
        get: function () {
            if (!_enabled) return null;
            if (_currentKey && _vms[_currentKey]) return _vms[_currentKey].vm;
            return null;
        },
        enumerable: true,
        configurable: true
    });

    Object.defineProperty(ttx.debug, 'vms', {
        get: function () {
            if (!_enabled) return {};
            var result = {};
            for (var k in _vms) {
                if (_vms.hasOwnProperty(k)) result[k] = _vms[k].vm;
            }
            return result;
        },
        enumerable: true,
        configurable: true
    });

    // ============================================================
    // 调试 API（受开关控制）
    // ============================================================

    function checkEnabled() {
        if (!_enabled) {
            ttx.console.warn('[DEBUG] 调试模式未开启，请执行 ttx.debug.open = true 或在 URL 加 ?debug=true');
            return false;
        }
        return true;
    }

    /** 列出所有已注册的 VM */
    ttx.debug.list = function () {
        if (!checkEnabled()) return;
        console.table(Object.values(_vms).map(function (v) {
            var mode = '?';
            try { mode = v.vm._get_data?.('currentMode') || '?'; } catch (e) {}
            return { key: v.key, title: v.title, mode: mode };
        }));
    };

    /** 切换到指定 VM（用于多页签场景切换调试目标） */
    ttx.debug.switchTo = function (key) {
        if (!checkEnabled()) return;
        if (_vms[key]) {
            _currentKey = key;
            ttx.console.log('[DEBUG] 已切换到 VM: ' + key + ' (' + (_vms[key].title || '') + ')');
        } else {
            ttx.console.warn('[DEBUG] 未找到 VM: ' + key);
        }
    };

    function getVm() {
        var vm = ttx.debug.vm;
        if (!vm) { ttx.console.warn('[DEBUG] 没有活跃的 VM，请先打开一个页面'); return null; }
        return vm;
    }

    /** 保存 — 走命令系统：beforeSave 校验 → API → afterSave */
    ttx.debug.save = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('save', { vm: vm });
    };

    /** 编辑 — 备份数据 → 切换到编辑态 */
    ttx.debug.edit = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('edit', { vm: vm });
    };

    /** 取消 — 还原备份数据 → 切换回浏览态 */
    ttx.debug.cancel = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('cancel', { vm: vm });
    };

    /** 返回 — beforeBack → requestClose */
    ttx.debug.back = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('back', { vm: vm });
    };

    /** 刷新 */
    ttx.debug.refresh = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('refresh', { vm: vm });
    };

    /** 删除 — beforeDelete 校验 → API → afterDelete */
    ttx.debug.delete = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('delete', { vm: vm });
    };

    /** 上一张单据 */
    ttx.debug.prevBill = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('prevbill', { vm: vm });
    };

    /** 下一张单据 */
    ttx.debug.nextBill = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('nextbill', { vm: vm });
    };

    /** 提交 — beforeSubmit 校验 → API → afterSubmit */
    ttx.debug.submit = function () {
        if (!checkEnabled()) return;
        var vm = getVm();
        if (!vm) return;
        ttx.command.execute('submit', { vm: vm });
    };
})();
